/**
 * 게시물의 작성자·참여 계정을 원본 필드에서 고르는 규칙.
 *
 * ES `_source` 원본과 `originalData` 보존분 중 살아 있는 쪽을 골라 계정/작성자를 세운다.
 * 소비처가 `moment-ingest` · `moment-transform` 이라 같은 폴더에 둔다.
 */
import type { SocialPostInput } from 'src/utils/moments/domain/social-post-input';

function normalizeParticipantSegment(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * author 비었을 때 쓸 account (account → username 순, 원문 그대로)
 * @param {{ account?: string|null, username?: string|null }} fields
 * @returns {string}
 */
export function resolveAccountForAuthorFallback(
  fields: { account?: string | null; username?: string | null } = {},
): string {
  const account = fields.account != null ? String(fields.account) : '';
  if (account.length >= 1) {
    return account;
  }
  const username = fields.username != null ? String(fields.username) : '';
  if (username.length >= 1) {
    return username;
  }
  return '';
}

/**
 * author.length >= 1 이면 그대로, 아니면 account(없으면 username) 복제
 * @param {{ author?: string|null, account?: string|null, username?: string|null }} fields
 * @returns {string}
 */
export function resolveRawAuthorFromSourceFields(
  fields: { author?: string | null; account?: string | null; username?: string | null } = {},
): string {
  const author = fields.author != null ? String(fields.author) : '';
  if (author.length >= 1) {
    return author;
  }
  return resolveAccountForAuthorFallback(fields);
}

/**
 * 게시물 표시용 author — ingest/카드/엑셀 공통
 * @param {Object} post
 * @returns {Object}
 */
export function applyParticipantAuthorFields<T extends SocialPostInput>(
  post: T | null | undefined,
): T | null | undefined {
  if (!post || typeof post !== 'object') {
    return post;
  }

  const resolvedAuthor = resolveRawAuthorFromSourceFields({
    author: post.author,
    account: post.account,
    username: post.username,
  });

  if (resolvedAuthor === post.author) {
    return post;
  }

  return { ...post, author: resolvedAuthor };
}

/**
 * ES participant_key와 동일: account는 플랫폼에 있을 때만(없으면 빈 구간).
 * transformPostData가 account←author 로 채운 경우는 account 없음으로 본다.
 */
function resolveParticipantAccount(
  post: SocialPostInput | null | undefined,
  author: string,
): string {
  const raw = post?._source ?? post?.originalData ?? null;
  if (raw) {
    const fromRaw = normalizeParticipantSegment(raw.account);
    if (fromRaw) return fromRaw;
  }
  const account = normalizeParticipantSegment(post?.account);
  if (!account || account === author) return '';
  return account;
}

/**
 * 참여 사용자(고유 계정) 집계 키 — source-account-author
 * author는 항상 있고, account는 소스별로 있을 수 있음(없으면 `src--author`).
 * @param {Object} post
 * @returns {string}
 */
export function buildPostParticipantKey(post: SocialPostInput | null | undefined): string {
  const source = normalizeParticipantSegment(
    post?.source
    ?? post?.platform
    ?? post?._source?.source
    ?? post?.originalData?.source,
  );
  const authorRaw = normalizeParticipantSegment(
    post?.author
    ?? post?.username
    ?? post?._source?.author
    ?? post?.originalData?.author,
  );
  const account = resolveParticipantAccount(post, authorRaw);
  const author = authorRaw || account;
  if (!source && !account && !author) return '';
  return `${source}-${account}-${author}`;
}
